import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Images } from 'docs/assets/data/images';
import { DatetimeHelper } from 'src/app/_core/helpers/datetime.helper';
import { CommonService } from 'src/app/_core/services/common.service';
import { AdminRoutes, SettingRoutes } from 'src/app/admin/admin.routes';
import { AppRoutes } from 'src/app/app.routes';
import { AlertType } from 'src/app/shared/components/alert/alert.type';
import { PublicRoutes } from '../../public.routes';
import { pageTransition } from 'src/app/shared/utils/animations';
import { CommonModule } from '@angular/common';
import { UtilitiesService } from 'src/app/_core/services/utilities.service';
import { AuthService } from '../auth.service';
import { SessionService } from 'src/app/shared/services/sessionStorage.service';

@Component({
  selector: 'app-otp',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule,
    ],
  templateUrl: './otp.component.html',
  animations: [pageTransition],
  styleUrl: './otp.component.css',
})
export class OtpComponent implements OnInit {
  readonly signinBannerImage: string = Images.bannerLogo;

  isLoading: boolean = false;
  readonly publicRoutes = PublicRoutes;
  readonly currentYear: number = DatetimeHelper.currentYear;
  submitted = false;
  error: string | null = null;
  otpForm!: FormGroup;

  showResend: boolean = false;
  minutes: number = 5;
  seconds: number = 0;
  private timerInterval: any;
  
  // Clé pour le stockage local du timer
  private readonly TIMER_STORAGE_KEY = 'otp_resend_timer_data';

  serverErrors: string[] = [];

  constructor(
    public commonService: CommonService,
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private sessionStorage: SessionService,
    private utilities: UtilitiesService
  ) {}

  // Méthode pour initialiser le timer au chargement de la page
  private initializeTimer() {
    const savedTimerData = this.getTimerData();
    
    if (savedTimerData && savedTimerData.isActive) {
      const now = Date.now();
      const timeElapsed = Math.floor((now - savedTimerData.startTime) / 1000);
      const totalSeconds = savedTimerData.totalDuration;
      const remainingSeconds = totalSeconds - timeElapsed;
      
      if (remainingSeconds > 0) {
        // Le timer est encore actif, le reprendre
        this.showResend = true;
        this.minutes = Math.floor(remainingSeconds / 60);
        this.seconds = remainingSeconds % 60;
        this.continueTimer();
      } else {
        // Le timer est expiré
        this.stopTimer();
      }
    }
  }

  // Méthode pour démarrer le timer
  startTimer() {
    if (this.showResend) return;

    this.showResend = true;
    this.minutes = 5;
    this.seconds = 0;
    
    const totalDuration = 5 * 60; // 5 minutes en secondes
    const startTime = Date.now();
    
    // Sauvegarder les données du timer
    this.saveTimerData({
      isActive: true,
      startTime: startTime,
      totalDuration: totalDuration
    });

    // Nettoyer tout intervalle existant
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      if (this.seconds > 0) {
        this.seconds--;
      } else {
        if (this.minutes > 0) {
          this.minutes--;
          this.seconds = 59;
        } else {
          this.stopTimer();
        }
      }
    }, 1000);
  }

  // Méthode pour continuer un timer existant
  private continueTimer() {
    // Nettoyer tout intervalle existant
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      if (this.seconds > 0) {
        this.seconds--;
      } else {
        if (this.minutes > 0) {
          this.minutes--;
          this.seconds = 59;
        } else {
          this.stopTimer();
        }
      }
    }, 1000);
  }

  // Méthode pour arrêter le timer
  stopTimer() {
    clearInterval(this.timerInterval);
    this.showResend = false;
    this.minutes = 0;
    this.seconds = 0;
    
    // Supprimer les données du timer du stockage
    this.clearTimerData();
  }

  // Méthodes utilitaires pour le stockage
  private saveTimerData(data: any) {
    try {
      localStorage.setItem(this.TIMER_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du timer:', error);
    }
  }

  private getTimerData() {
    try {
      const data = localStorage.getItem(this.TIMER_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Erreur lors de la récupération du timer:', error);
      return null;
    }
  }

  private clearTimerData() {
    try {
      localStorage.removeItem(this.TIMER_STORAGE_KEY);
    } catch (error) {
      console.error('Erreur lors de la suppression du timer:', error);
    }
  }

  ngOnInit(): void {
    // Initialiser le timer avant de potentiellement en démarrer un nouveau
    this.initializeTimer();
    
    // Si aucun timer n'était actif, en démarrer un nouveau
    if (!this.showResend) {
      this.startTimer();
    }
    
    console.log('--- Session Storage Debug ---');
    console.log('Direct sessionStorage:', sessionStorage.getItem('currentUser'));
    
    console.log('Via service:', this.sessionStorage.get('currentUser'));
    console.log('Current user value:', this.authService.currentUserValue);
    
    let email = '';
    try {
      const storedUser = this.sessionStorage.get('currentUser');
      if (storedUser) {
        const userData = typeof storedUser === 'string' ? JSON.parse(storedUser) : storedUser;
        email = userData.email || '';
      }
      
      if (!email) {
        const directUser = sessionStorage.getItem('currentUser');
        if (directUser) {
          const userData = JSON.parse(directUser);
          email = userData.email || '';
        }
      }

      console.log('Recovered email for OTP form:', email);
    } catch (e) {
      console.error('Error recovering email:', e);
    }
    

    this.otpForm = this.formBuilder.group({
      codeOtp: [
        '',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(4),
          Validators.pattern(/^\d+$/),
        ],
      ],
      email: [email] 
    });
  }
  
  formatInput(event: Event) {
    const input = event.target as HTMLInputElement;
    // Permet uniquement les chiffres
    input.value = input.value.replace(/[^0-9]/g, '');
  }
  
  onSubmit() {
    this.isLoading = true
    this.submitted = true;
    this.error = '';

    if (this.otpForm.invalid) {
      this.error = 'email et mot de passe sont requis et doivent être valides.';
      this.isLoading = false;
      return;
    }

    const { codeOtp, email } = this.otpForm.value;

    // Call the email service
    this.authService.otp(codeOtp, email).subscribe(
      (res: any) => {
        if (res && !res['hasError']) {
          console.log('resAAA', res);
          // Arrêter le timer en cas de succès
          this.stopTimer();
          this.router.navigate(['admin/seance']);
        } else {
          console.log('connexion valid', this.f);

          // Check the response for specific status code or error messages
          if (res['status'] && res['status']['code'] == 450) {
            this.utilities.success(res['status']['message'], 'success!');
          } else {
            this.utilities.showNotification(
              res.status?.message || 'Erreur lors de la vérification du code OTP',
              'error'
            );
          }
        }
      },
      (error) => {
        console.error('Error during email:', error);
        this.utilities.error(
          'Opération échouée',
          'email ou Mot de passe incorrecte !'
        );
      },
      () => {
        this.isLoading = false; 
      }
    );
  }
  
  get f() {
    return this.otpForm.controls;
  }

  protected readonly AlertType = AlertType;

  protected onOtpSubmitHandler = (event: SubmitEvent) => {
    event.preventDefault();
    this.isLoading = true;

    setTimeout(() => {
      this.isLoading = false;
      this.router.navigate([PublicRoutes.Home]);
    }, 3000);
  };

  renvoyerOtp() {
    const email = this.otpForm.value.email;
    
    if (!email) {
      this.utilities.error(
        'Email manquant',
        'Impossible de renvoyer le code OTP sans adresse email.'
      );
      return;
    }
    
    this.isLoading = true;
    
    this.authService.resendOtp(email).subscribe(
      (res: any) => {
        if (res && !res['hasError']) {
          this.utilities.success(
            'Un nouveau code OTP a été envoyé à votre adresse email.',
            'Succès!'
          );
          // Redémarrer le timer après un renvoi réussi
          this.stopTimer(); // Arrêter le timer actuel
          this.startTimer(); // Redémarrer un nouveau timer
        } else {
          this.utilities.error(
            res['status']?.['message'] || 'Opération échouée',
            'Erreur lors de l\'envoi du code OTP!'
          );
        }
      },
      (error) => {
        console.error('Erreur lors du renvoi du code OTP:', error);
        this.utilities.error(
          'Opération échouée',
          'Impossible de renvoyer le code OTP. Veuillez réessayer.'
        );
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  protected onAlertCloseHandler = (e: any) => {
    this.serverErrors = [];
  };

  ngOnDestroy(): void {
    // Nettoyer l'intervalle mais ne pas supprimer les données du localStorage
    // pour permettre la persistance du timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
}